import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { SectionHeader } from "@/shared/ui/SectionHeader";
import { TextField, type TextFieldTone } from "@/shared/ui/TextField";
import { isPasswordValid } from "@/public/features/org/composables/orgNewDraft";
import { FullScreenDialog } from "@/shared/ui/FullScreenDialog";
import { PrivacyPolicyBody } from "@/public/features/site/pages/PrivacyPage";
import type { OrgNewDraft } from "@/public/features/org/types";
import { CONSENTS, ORG_LAST_STEP } from "@/public/features/org/constants/orgNew";
import {
  OrgNewPcShell,
  PcFieldRow,
  PC_ASSISTIVE_BUTTON_CLASS,
  PC_PRIMARY_BUTTON_CLASS,
} from "@/public/features/org/ui/orgNewPc/parts";
import { isEmail } from "@/public/features/org/ui/orgNew/parts";

type Feedback = { tone: TextFieldTone; message: string | null };

/** 비밀번호 칸의 문구와 색 — 시안 666:28889 «인풋 변화». */
function passwordFeedback(draft: OrgNewDraft): Feedback {
  if (draft.passwordMessage !== null)
    return { tone: "error", message: draft.passwordMessage };
  if (isPasswordValid(draft.password))
    return { tone: "success", message: "사용 가능한 비밀번호입니다." };
  return { tone: "default", message: null };
}

/** 비밀번호 확인 칸의 문구와 색 — 시안 666:28893 «인풋 변화». */
function confirmFeedback(draft: OrgNewDraft): Feedback {
  if (draft.hasConfirmError)
    return { tone: "error", message: "비밀번호가 일치하지 않습니다." };
  if (draft.passwordConfirm.length > 0 && draft.passwordConfirm === draft.password)
    return { tone: "success", message: "비밀번호가 확인되었습니다." };
  return { tone: "default", message: null };
}

/** PC 두 번째 페이지 — 모바일 4단계(담당자 · 수정용 비밀번호 · 동의)에 대응한다(시안 1288:20274). */
export function OrgNewPcManager({ draft }: { draft: OrgNewDraft }) {
  const navigate = useNavigate();
  const [policyOpen, setPolicyOpen] = useState(false);

  const canSubmit = draft.firstIncompleteStep > ORG_LAST_STEP;

  const emailMessage =
    draft.managerEmail.length > 0 && !isEmail(draft.managerEmail)
      ? "이메일 형식이 맞지 않습니다."
      : undefined;

  const password = passwordFeedback(draft);
  const confirm = confirmFeedback(draft);

  return (
    <OrgNewPcShell
      title="담당자 입력하기"
      active="manager"
      error={draft.error}
      footer={
        <>
          <button
            type="button"
            className={`${PC_ASSISTIVE_BUTTON_CLASS} w-[143px]`}
            onClick={() => navigate("/orgs/new")}
          >
            이전
          </button>
          <button
            type="button"
            className={`${PC_PRIMARY_BUTTON_CLASS} w-[250px]`}
            disabled={!canSubmit || draft.isSubmitting}
            onClick={draft.submit}
          >
            {draft.isSubmitting ? "등록하는 중…" : "등록하고 해결카드 만들기"}
          </button>
        </>
      }
    >
      <div className="flex w-full flex-col items-start gap-6">
        <div className="flex w-full flex-col items-start gap-[46px]">
          <section className="flex w-full flex-col items-start gap-6">
            <SectionHeader
              no="01"
              title="담당자 정보를 알려주세요."
              desc="문제와 매칭되면 입력한 연락처로 안내해 드려요."
            />
            <div className="flex w-full flex-col gap-2.5">
              <PcFieldRow>
                <TextField
                  size="large"
                  label="담당자 이름"
                  autoComplete="name"
                  placeholder="이름을 입력해주세요."
                  value={draft.managerName}
                  onChange={(e) => draft.setManagerName(e.target.value)}
                />
                <TextField
                  size="large"
                  label="담당자 휴대폰 번호"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="010-0000-0000"
                  message="해당 정보는 공개되지 않으며 관리자만 확인할 수 있습니다."
                  value={draft.managerPhone}
                  onChange={(e) => draft.setManagerPhone(e.target.value)}
                />
              </PcFieldRow>
              <div className="w-full max-w-[490px]">
                <TextField
                  size="large"
                  label="담당자 이메일"
                  type="email"
                  autoComplete="email"
                  placeholder="haebom@examle.com"
                  value={draft.managerEmail}
                  onChange={(e) => draft.setManagerEmail(e.target.value)}
                  {...(emailMessage !== undefined
                    ? { message: emailMessage }
                    : {})}
                />
              </div>
            </div>
          </section>

          <section className="flex w-full flex-col items-start gap-6">
            <SectionHeader
              no="02"
              title="수정용 비밀번호를 설정해주세요."
              desc="등록 정보를 수정할 때 사용할 비밀번호입니다."
            />
            <PcFieldRow>
              <TextField
                size="large"
                label="비밀번호"
                type="password"
                autoComplete="new-password"
                placeholder="5자 이상, 영문과 숫자를 포함해 주세요."
                value={draft.password}
                onChange={(e) => draft.setPassword(e.target.value)}
                tone={password.tone}
                {...(password.message !== null
                  ? { message: password.message }
                  : {})}
              />
              <TextField
                size="large"
                label="비밀번호 확인"
                type="password"
                autoComplete="new-password"
                placeholder="비밀번호를 다시 입력해 주세요."
                value={draft.passwordConfirm}
                onChange={(e) => draft.setPasswordConfirm(e.target.value)}
                tone={confirm.tone}
                {...(confirm.message !== null ? { message: confirm.message } : {})}
              />
            </PcFieldRow>
          </section>
        </div>

        <div className="flex w-full flex-col gap-3">
          {CONSENTS.map((c) => (
            <label
              key={c.key}
              className="ui-pressable flex cursor-pointer items-center gap-2"
            >
              <input
                type="checkbox"
                className="m-0 h-5 w-5 shrink-0 rounded accent-violet-500"
                checked={draft.consents.includes(c.key)}
                onChange={() => draft.toggleConsent(c.key)}
              />
              <span className="min-w-0 flex-1 shrink-0 grow basis-0 text-body-sm text-black-600">
                {c.label}
                {c.detail ? (
                  <button
                    type="button"
                    className="ml-2 text-black-100 underline"
                    onClick={(e) => {
                      e.preventDefault();
                      setPolicyOpen(true);
                    }}
                  >
                    자세히
                  </button>
                ) : null}
              </span>
            </label>
          ))}
        </div>
      </div>

      {policyOpen && (
        <FullScreenDialog
          title="개인정보 처리방침"
          onClose={() => setPolicyOpen(false)}
        >
          <PrivacyPolicyBody />
        </FullScreenDialog>
      )}
    </OrgNewPcShell>
  );
}
